import React, { useCallback } from 'react';
import { useNavigate } from '@tanstack/react-router';

import { InsuranceCard } from '@/components/InsuranceCard.tsx';
import { NavigationBar } from '@/components/NavigationBar.tsx';
import StepperCard from '@/components/common/StepperCard.tsx';
import { CardContent, CardHeader, CardTitle } from '@/components/ui/card.tsx';
import { useSessionSave } from '@/hooks/use-session-save.ts';
import { useSessionStore } from '@/hooks/use-session-store/use-session-store.ts';
import type { RoutePath } from '@/lib/wizard/wizard';
import {
  getCarrierTypesNeedingInput,
  removeInsurance,
  setCarrierTypes,
} from '@/types/insurance-builder-legacy.ts';
import {
  CARRIER_TYPE,
  type CarrierType,
  SERVICED_CARRIER_TYPES,
  type VerifiedInsurance,
  carrierTypesShareFamily,
} from '@/types/insurance.type.ts';

import InsuranceInputPrepForm from './components/InsuranceInputPrepForm.tsx';

export function InsuranceOptions() {
  const navigate = useNavigate();
  const { saveSession } = useSessionSave();
  const {
    insuranceBuilder,
    patientRecipient,
    patientFirstName,
    patientLastName,
    patientDateOfBirth,
    patientGender,
  } = useSessionStore();

  const patient = {
    recipient: patientRecipient as 'me' | 'someone-else',
    firstName: patientFirstName,
    lastName: patientLastName,
    dateOfBirth: patientDateOfBirth,
    gender: patientGender as 'male' | 'female',
  };

  const deleteInsurance = useCallback(
    (insurance: VerifiedInsurance) => {
      useSessionStore
        .getState()
        .updateState({ insuranceBuilder: removeInsurance(insuranceBuilder, insurance) });
    },
    [insuranceBuilder]
  );

  const continueInsuranceFlow = useCallback(
    (selectedCarrierTypes: Set<CarrierType>) => {
      const retainedVerifiedCarrierTypes = insuranceBuilder.verifiedInsurances.map(
        (insurance: VerifiedInsurance) =>
          insuranceBuilder.carrierTypes.find((type) =>
            carrierTypesShareFamily(type, insurance.carrier.carrierType)
          ) ?? insurance.carrier.carrierType
      );
      const expectedCarrierTypes = new Set([
        ...selectedCarrierTypes,
        // A patient not removing the verified insurance implies they still
        // claim to have that carrier family. Preserve the legacy base type the
        // patient selected instead of replacing it with the managed API result.
        ...retainedVerifiedCarrierTypes,
      ]);

      const updatedBuilder = setCarrierTypes(insuranceBuilder, expectedCarrierTypes);

      useSessionStore.getState().updateState({ insuranceBuilder: updatedBuilder });

      // Auto-save session (non-blocking)
      saveSession();

      const missingCarrierTypes = getCarrierTypesNeedingInput(updatedBuilder);

      if (missingCarrierTypes.length > 0)
        return navigate({ to: `/insurance/input?carrierType=${missingCarrierTypes[0]}` });

      const noPublicCarriersSelected = updatedBuilder.carrierTypes.every(
        (type: CarrierType) => !SERVICED_CARRIER_TYPES.has(type)
      );

      if (noPublicCarriersSelected) {
        return navigate({ to: '/insurance/ineligible' });
      }

      const needsVerification = updatedBuilder.queuedInsurances.length > 0;

      if (needsVerification) {
        return navigate({ to: '/insurance/verify-pending' as RoutePath });
      }

      // missingCarrierTypes.size === 0 && !onlyPrivateInsurance
      return navigate({ to: '/insurance/finalize' });
    },
    [insuranceBuilder, navigate, saveSession]
  );

  const toInsuranceStart = () => navigate({ to: '/insurance/pre-discovery' });

  return (
    <section>
      <NavigationBar onBack={toInsuranceStart} backLabel="Insurance info" stepIndicator="3 of 4" />
      <StepperCard currentStep={3} totalSteps={4}>
        <CardHeader>
          <CardTitle>{headingText(insuranceBuilder.verifiedInsurances)}</CardTitle>
        </CardHeader>

        <CardContent className="space-y-8">
          {insuranceBuilder.verifiedInsurances.length > 0 && (
            <div className="space-y-4.5">
              {insuranceBuilder.verifiedInsurances.map(
                (insurance: VerifiedInsurance, index: number) => (
                  <InsuranceCard
                    key={index}
                    insurance={insurance}
                    policyHolder={patient}
                    onRemove={() => deleteInsurance(insurance)}
                  />
                )
              )}
            </div>
          )}

          <InsuranceInputPrepForm
            carrierTypes={insuranceBuilder.preDiscoveryCarrierTypes}
            insurances={insuranceBuilder.verifiedInsurances}
            onSubmit={continueInsuranceFlow}
          />
        </CardContent>
      </StepperCard>
    </section>
  );
}

function headingText(insurances: VerifiedInsurance[]): string {
  const hasMedicare = insurances.some((i) =>
    carrierTypesShareFamily(i.carrier.carrierType, CARRIER_TYPE.MEDICARE)
  );
  const hasMedicaid = insurances.some((i) =>
    carrierTypesShareFamily(i.carrier.carrierType, CARRIER_TYPE.MEDICAID)
  );

  if (hasMedicare && !hasMedicaid) {
    return 'We only found your Medicare plan';
  } else if (hasMedicaid && !hasMedicare) {
    return 'We only found your Medicaid plan';
  } else {
    return "We couldn't find your insurance info";
  }
}
